// src/components/OnlyCryptoHeader.jsx

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import {
    setLink,
    syncTelegramMeta,
    getPromoterLinkDisplay,
    getPromoterAvatarSrc,
    normalizeTelegramLink,
    isTelegramPromoterLink,
    TELEGRAM_LINK_ONLY_MESSAGE,
    TELEGRAM_NOT_FOUND_MESSAGE,
} from '../../../store/slices/promoTelegramSlice';
import { setBudget, setSelectedBlockchain, setFoundToken } from '../../../store/slices/budgetSlice';
import { setLink as setFormLink, syncWithBudget, syncWithCrystals, syncWithPromoTime, setParticipantsCount, setPrizePlacesCount, setMaxParticipantsCount, setCustomPlaceWinners, setCustomPlaceBudgets } from '../../../store/slices/promoFormSlice';
import getIcon from '../../../utils/Icon';
import CoinBadge from '../../coin_badge/CoinBadge';
import CrystalsApi from '../../../api/CrystalsApi';
import feeApi from '../../../api/feeApi';
import { selectSelectedDay } from '../../../store/slices/promoTimeSlice';
import { useKeyboard } from '../../Keyboard';
import AccordionArrow from '../../icons/AccordionArrow';
import SettingsIcon from '../../icons/SettingsIcon';
import CrownIcon from '../../icons/CrownIcon';
import { PromoSnackbar, usePromoSnackbar } from './PromoSnackbar';
import { usePromoGuide, useAdvanceGuideOnKeyboardClose } from '../PromoGuideContext';
import { getCrystalRarityTier } from '../../../data/crystalRarityTiers';
import { addRecentLink, getRecentLinks, PROMOTER_TG_LINK_RECENT_KEY } from '../../../utils/keyboardRecentLinks';
import Lottie from 'lottie-react';
import swipeAnim from '../../../assets/lottie/swipe.json';
import {
    distributeWinnersByPercent,
    redistributePlaceWinner,
    distributeBudgetByPercent,
    redistributePlaceBudget,
    isMatchingPlaceTotals,
    resolveWinPercents,
    resolveBudgetPercents,
    distributePercentsFromCounts,
} from '../../../utils/placeWinners';

const IMAGE_BASE_URL = 'https://api.duotetra.quest/uploads/gems/';

const SUBSCRIPT_DIGITS = '₀₁₂₃₄₅₆₇₈₉';

const toSubscript = (value) =>
    String(value).split('').map((digit) => SUBSCRIPT_DIGITS[Number(digit)]).join('');

const formatLeadingZeroDecimal = (num) => {
    if (!(num > 0 && num < 1)) return null;

    const normalized = num.toFixed(20).replace(/(\.\d*?)0+$/, '$1');
    const match = normalized.match(/^0\.(0+)([1-9]\d*)$/);
    if (!match) return null;

    const zeroCount = match[1].length;
    if (zeroCount < 2) return null;

    const significant = match[2].slice(0, 4);
    return `0.0${toSubscript(zeroCount)}${significant}`;
};

const formatThousands = (value) => {
    if (value == null || value === '') return '';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);

    if (Number.isInteger(num)) {
        return String(num).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    }

    const normalized = num.toFixed(4).replace(/\.?0+$/, '');
    const [intPart, decPart] = normalized.split('.');
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt;
};

const formatPresetBudgetCell = (value) => {
    if (value == null || value === '') return '';
    const num = Number(value);
    if (Number.isNaN(num)) return '';

    if (num >= 1_000_000_000_000) {
        return `${(num / 1_000_000_000_000).toFixed(2).replace(/\.?0+$/, '')} T`;
    }
    if (num >= 1_000_000_000) {
        return `${(num / 1_000_000_000).toFixed(2).replace(/\.?0+$/, '')} B`;
    }
    if (num >= 1_000_000) {
        return `${(num / 1_000_000).toFixed(2).replace(/\.?0+$/, '')} M`;
    }
    if (num >= 1000) {
        return `${(num / 1000).toFixed(2).replace(/\.?0+$/, '')} K`;
    }
    if (num >= 1) {
        return num.toFixed(2).replace(/\.?0+$/, '');
    }
    if (num > 0 && num < 1) {
        const subscriptFormatted = formatLeadingZeroDecimal(num);
        if (subscriptFormatted) return subscriptFormatted;
        return num.toFixed(2).replace(/\.?0+$/, '');
    }
    return String(num);
};

const formatCompactBudget = (value) => {
    if (value == null || value === '') return '';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);

    if (num >= 1_000_000_000_000) {
        return `${(num / 1_000_000_000_000).toFixed(2).replace(/\.?0+$/, '')} T`;
    }
    if (num >= 1_000_000_000) {
        return `${(num / 1_000_000_000).toFixed(2).replace(/\.?0+$/, '')} B`;
    }
    if (num >= 1_000_000) {
        return `${(num / 1_000_000).toFixed(2).replace(/\.?0+$/, '')} M`;
    }
    if (num >= 1000) {
        return `${(num / 1000).toFixed(2).replace(/\.?0+$/, '')} k`;
    }
    return formatThousands(num);
};

const formatRewardAmount = (amount) => {
    if (amount == null) return '';
    if (typeof amount !== 'number' || Number.isNaN(amount)) return String(amount);
    if (Number.isInteger(amount)) return String(amount);

    const subscriptFormatted = formatLeadingZeroDecimal(amount);
    if (subscriptFormatted) return subscriptFormatted;

    return amount.toFixed(8).replace(/\.?0+$/, '');
};

const formatTableRewardAmount = (rewardAmount) => {
    if (rewardAmount == null) return '';
    if (typeof rewardAmount !== 'number' || Number.isNaN(rewardAmount)) {
        return String(rewardAmount);
    }
    if (rewardAmount > 0 && rewardAmount < 1) {
        return formatRewardAmount(rewardAmount);
    }

    const abs = Math.abs(rewardAmount);
    let decimals = 4;

    if (abs >= 10_000) decimals = 0;
    else if (abs >= 1_000) decimals = 1;
    else if (abs >= 100) decimals = 2;
    else if (abs >= 10) decimals = 3;

    const formatted = decimals === 0
        ? String(Math.round(abs))
        : abs.toFixed(decimals).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
    return rewardAmount < 0 ? `-${formatted}` : formatted;
};

const formatCompactCount = (value) => {
    if (value == null || value === '') return '';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);

    if (num >= 1_000_000_000_000) {
        return `${(num / 1_000_000_000_000).toFixed(2).replace(/\.?0+$/, '')} T`;
    }
    if (num >= 1_000_000_000) {
        return `${(num / 1_000_000_000).toFixed(2).replace(/\.?0+$/, '')} B`;
    }
    if (num >= 1_000_000) {
        return `${(num / 1_000_000).toFixed(2).replace(/\.?0+$/, '')} M`;
    }
    if (num >= 1000) {
        return `${(num / 1000).toFixed(2).replace(/\.?0+$/, '')} K`;
    }
    if (num >= 1) {
        return num % 1 === 0 ? String(Math.round(num)) : num.toFixed(4).replace(/\.?0+$/, '');
    }
    if (num > 0 && num < 1) {
        const subscriptFormatted = formatLeadingZeroDecimal(num);
        if (subscriptFormatted) return subscriptFormatted;
        return num.toFixed(4).replace(/\.?0+$/, '');
    }
    return String(num);
};

export default function OnlyCryptoHeader() {
    const dispatch = useDispatch();
    const { link, data, loading: tgLoading } = useSelector(s => s.promoTelegram);
    const { budget, foundToken, selectedBlockchain } = useSelector(s => s.budget);
    const { items: crystals, selectedId, loading: crystalsLoading } = useSelector(s => s.crystals);
    const selectedDay = useSelector(selectSelectedDay);
    const formLink = useSelector(s => s.promoForm.link);
    const selectedCategory = crystals.find(cat => cat.id === selectedId);
    const LINK_PLACEHOLDER = 'Promoter TG link';
    const LEGACY_LINK_PLACEHOLDER = 'Promoter link';
    const normalizePromoterLink = useCallback((value) => {
        const trimmed = (value || '').trim();
        if (!trimmed) return '';
        const lower = trimmed.toLowerCase();
        if (lower === LINK_PLACEHOLDER.toLowerCase() || lower === LEGACY_LINK_PLACEHOLDER.toLowerCase()) return '';
        return trimmed;
    }, []);

    const promoterLinkDisplay = useMemo(() => {
        if (!link || link === LINK_PLACEHOLDER || link === LEGACY_LINK_PLACEHOLDER) {
            return LINK_PLACEHOLDER;
        }
        return getPromoterLinkDisplay(link, data, LINK_PLACEHOLDER);
    }, [link, data]);

    const hasResolvedPromoterLink = promoterLinkDisplay !== LINK_PLACEHOLDER;

    // Кастомные значения участников/победителей из Redux
    const customParticipants = useSelector(s => s.promoForm.participantsCount);
    const customWinners = useSelector(s => s.promoForm.prizePlacesCount);
    const customPlaceWinners = useSelector(s => s.promoForm.customPlaceWinners);
    const customPlaceBudgets = useSelector(s => s.promoForm.customPlaceBudgets);
    const { snackbar, snackbarAnimation, showSnackbar, closeSnackbar } = usePromoSnackbar();
    const [showPromoterTooltip, setShowPromoterTooltip] = useState(false);
    const [showPrizePoolTooltip, setShowPrizePoolTooltip] = useState(false);
    const { step: guideStep, completeTelegramLink, completePlayers, completeWinners, completeTierWinners, completeTierBudget } = usePromoGuide();
    const showLinkGuide = guideStep === 'telegramLink';
    const showPlayersGuide = guideStep === 'players';
    const showWinnersGuide = guideStep === 'winners';
    const showTierWinnersGuide = guideStep === 'tierWinners';
    const showTierBudgetGuide = guideStep === 'tierBudget';
    useAdvanceGuideOnKeyboardClose(showLinkGuide, completeTelegramLink);
    useAdvanceGuideOnKeyboardClose(showPlayersGuide, completePlayers);
    useAdvanceGuideOnKeyboardClose(showWinnersGuide, completeWinners);
    useAdvanceGuideOnKeyboardClose(showTierWinnersGuide, completeTierWinners);
    useAdvanceGuideOnKeyboardClose(showTierBudgetGuide, completeTierBudget);
    const [playersTouched, setPlayersTouched] = useState(false);
    const [winnersTouched, setWinnersTouched] = useState(false);

    const promoterRef = useRef(null);
    const prizePoolRef = useRef(null);

    useEffect(() => {
        if (!showPromoterTooltip) return;

        const handleClickOutside = (event) => {
            if (promoterRef.current && !promoterRef.current.contains(event.target)) {
                setShowPromoterTooltip(false);
            }
        };

        document.addEventListener('pointerdown', handleClickOutside);
        return () => document.removeEventListener('pointerdown', handleClickOutside);
    }, [showPromoterTooltip]);

    useEffect(() => {
        if (!showPrizePoolTooltip) return;

        const handleClickOutside = (event) => {
            if (prizePoolRef.current && !prizePoolRef.current.contains(event.target)) {
                setShowPrizePoolTooltip(false);
            }
        };

        document.addEventListener('pointerdown', handleClickOutside);
        return () => document.removeEventListener('pointerdown', handleClickOutside);
    }, [showPrizePoolTooltip]);

    // Синхронизируем категорию с promoFormSlice
    useEffect(() => {
        if (selectedCategory) {
            dispatch(syncWithCrystals({ selectedId, crystals }));
        }
    }, [selectedId, crystals, dispatch]);
    
    // Загружаем все типы гемов для иконок
    const [gemTypes, setGemTypes] = useState([]);
    
    useEffect(() => {
        CrystalsApi.getAllGems()
            .then(setGemTypes)
            .catch(err => console.error('Ошибка загрузки типов гемов:', err));
    }, []);

    // Строим массив URL-ов frontImage по реверснутым position
    const [positionIcons, setPositionIcons] = useState([]);
    useEffect(() => {
        if (!selectedCategory || gemTypes.length === 0) {
            setPositionIcons([]);
            return;
        }
        const count = selectedCategory.number;
        const icons = Array.from({ length: count }, (_, i) => {
            const pos = count - i;
            const gem = gemTypes.find(g => g.position === pos);
            return gem
                ? IMAGE_BASE_URL + gem.frontImage
                : getIcon('diamond');
        });
        setPositionIcons(icons);
    }, [selectedCategory, gemTypes]);

    // Синхронизируем link из promoFormSlice с promoTelegramSlice при загрузке
    useEffect(() => {
        if (formLink && formLink !== link) {
            dispatch(setLink(formLink));
        }
    }, [formLink, link, dispatch]);

    // Синхронизируем данные с promoFormSlice
    useEffect(() => {
        dispatch(syncWithBudget({ budget, selectedBlockchain, foundToken }));
    }, [budget, selectedBlockchain, foundToken, dispatch]);

    useEffect(() => {
        if (selectedDay) {
            dispatch(syncWithPromoTime({ selectedDay }));
        }
    }, [selectedDay, dispatch]);

    // Рассчитываем комиссию (fee) по количеству участников
    const [fee, setFee] = useState(null);
    const [feeLoading, setFeeLoading] = useState(false);

    useEffect(() => {
        if (!selectedCategory) {
            setFee(null);
            return;
        }
        const count = customParticipants || selectedCategory.minParticipantsCount;
        if (!count) {
            setFee(null);
            return;
        }

        setFeeLoading(true);

        (async () => {
            try {
                const allFees = await feeApi.feeLevelsApi.getAll();
                if (!allFees?.length) {
                    setFee(null);
                    return;
                }

                // Только для криптовалюты (не гемы)
                const filtered = allFees
                    .filter(f => !f.onlyGem)
                    .sort((a, b) => a.max - b.max);

                if (!filtered.length) {
                    setFee(null);
                    return;
                }

                let resultFee = filtered[0]?.fee ?? 0;

                // Линейная интерполяция между уровнями
                for (let i = 1; i < filtered.length; i++) {
                    if (count <= filtered[i].max) {
                        const prev = filtered[i - 1];
                        const curr = filtered[i];
                        const inc = (curr.fee - prev.fee) / (curr.max - prev.max);
                        resultFee = prev.fee + inc * (count - prev.max);
                        break;
                    }
                }

                // Если count больше максимального уровня
                if (count > filtered[filtered.length - 1].max) {
                    resultFee = filtered[filtered.length - 1].fee;
                }

                setFee(resultFee.toFixed(2));
            } catch (e) {
                console.error('Ошибка расчёта fee:', e);
                setFee(null);
            } finally {
                setFeeLoading(false);
            }
        })();
    }, [selectedCategory, customParticipants]);

    const { openKeyboard, isOpen: isKeyboardOpen } = useKeyboard();
    const [activeEdit, setActiveEdit] = useState(null);
    const [activePresetModal, setActivePresetModal] = useState(null);
    const [compactCategoryView, setCompactCategoryView] = useState(false);

    useEffect(() => {
        if (!isKeyboardOpen) setActiveEdit(null);
    }, [isKeyboardOpen]);

    // Keep the edited winners/budget row visible above the custom keyboard
    useEffect(() => {
        if (!isKeyboardOpen || activeEdit == null || activeEdit.index == null) return undefined;

        let cancelled = false;

        const getScrollParent = (el) => {
            let node = el?.parentElement;
            while (node && node !== document.body) {
                const { overflowY } = getComputedStyle(node);
                if (/(auto|scroll|overlay)/.test(overflowY) && node.scrollHeight > node.clientHeight + 1) {
                    return node;
                }
                node = node.parentElement;
            }
            return document.scrollingElement || document.documentElement;
        };

        const scrollActiveRowIntoView = () => {
            if (cancelled) return;
            const row = document.querySelector(`[data-place-row="${activeEdit.index}"]`);
            if (!row) return;

            const kb = document.querySelector('[data-custom-keyboard="1"]');
            const kbHeight = kb?.getBoundingClientRect()?.height || 340;
            const topPad = 72;
            const bottomPad = 16;
            const viewBottom = window.innerHeight - kbHeight - bottomPad;
            const viewTop = topPad;
            const rect = row.getBoundingClientRect();

            let delta = 0;
            if (rect.bottom > viewBottom) {
                delta = rect.bottom - viewBottom;
            } else if (rect.top < viewTop) {
                delta = rect.top - viewTop;
            }
            if (Math.abs(delta) < 4) return;

            const container = getScrollParent(row);
            if (container === document.documentElement || container === document.body) {
                window.scrollBy({ top: delta, behavior: 'smooth' });
            } else {
                container.scrollTo({ top: container.scrollTop + delta, behavior: 'smooth' });
            }
        };

        // Wait for keyboard spring + layout; retry a couple times while height settles
        const timers = [
            setTimeout(scrollActiveRowIntoView, 80),
            setTimeout(scrollActiveRowIntoView, 220),
            setTimeout(scrollActiveRowIntoView, 420),
        ];

        return () => {
            cancelled = true;
            timers.forEach(clearTimeout);
        };
    }, [isKeyboardOpen, activeEdit]);

    // Telegram-инпут — через кастомную клавиатуру (без <input>)
    const handleOpenKeyboard = useCallback(() => {
        if (tgLoading) return;

        const username = window.Telegram?.WebApp?.initDataUnsafe?.user?.username;
        const selfLink = username ? `https://t.me/${username}` : '';
        const recent = getRecentLinks(PROMOTER_TG_LINK_RECENT_KEY);
        const suggestions = [selfLink, ...recent]
            .map((s) => String(s || '').trim())
            .filter(Boolean)
            .filter((link, idx, arr) => arr.indexOf(link) === idx);

        openKeyboard({
            type: 'qwerty',
            value: '',
            label: 'Promoter TG link',
            suggestions,
            suggestionsLabel: '',
            onChange: (finalValue) => {
                const normalizedValue = normalizePromoterLink(finalValue);
                if (!normalizedValue) return;
                if (!isTelegramPromoterLink(normalizedValue)) {
                    showSnackbar(TELEGRAM_LINK_ONLY_MESSAGE, 'error');
                    dispatch(setLink(''));
                    dispatch(setFormLink(''));
                    return;
                }
                const telegramLink = normalizeTelegramLink(normalizedValue);
                dispatch(setLink(telegramLink));
                dispatch(setFormLink(telegramLink));
                addRecentLink(PROMOTER_TG_LINK_RECENT_KEY, telegramLink);
            },
            onSubmit: async (finalValue) => {
                const normalizedValue = normalizePromoterLink(finalValue);
                if (!normalizedValue || !isTelegramPromoterLink(normalizedValue)) return;
                const telegramLink = normalizeTelegramLink(normalizedValue);
                addRecentLink(PROMOTER_TG_LINK_RECENT_KEY, telegramLink);
                try {
                    await dispatch(syncTelegramMeta(telegramLink)).unwrap();
                } catch (err) {
                    dispatch(setLink(''));
                    dispatch(setFormLink(''));
                    showSnackbar(
                        typeof err === 'string' ? err : TELEGRAM_NOT_FOUND_MESSAGE,
                        'error'
                    );
                }
            },
            liveUpdate: false,
        });
    }, [openKeyboard, dispatch, tgLoading, normalizePromoterLink, showSnackbar]);

    // Редактирование количества участников
    const handleEditParticipants = useCallback(() => {
        if (!selectedCategory) return;
        setPlayersTouched(true);
        const min = selectedCategory.minParticipantsCount || 1;
        const max = 10000;
        openKeyboard({
            type: 'numeric',
            value: String(customParticipants || min),
            label: `Players (min ${min}, max ${max.toLocaleString('en-US').replace(/,/g, ' ')})`,
            onChange: (val) => {
                const num = parseInt(val, 10);
                if (!isNaN(num) && num > 0) {
                    const clamped = Math.min(Math.max(num, min), max);
                    dispatch(setParticipantsCount(clamped));
                    dispatch(setMaxParticipantsCount(clamped));
                    if ((customWinners || 0) > clamped) {
                        dispatch(setPrizePlacesCount(clamped));
                    }
                }
            },
            liveUpdate: false,
        });
    }, [openKeyboard, selectedCategory, customParticipants, customWinners, dispatch]);

    // Редактирование количества победителей
    const handleEditWinners = useCallback(() => {
        if (!selectedCategory) return;
        setWinnersTouched(true);
        const min = selectedCategory.minPrizePlacesCount || 1;
        const max = Math.min(
            customParticipants || selectedCategory.minParticipantsCount || 10000,
            10000
        );
        openKeyboard({
            type: 'numeric',
            value: String(customWinners || min),
            label: `Winners (min ${min}, max ${max})`,
            onChange: (val) => {
                const num = parseInt(val, 10);
                if (!isNaN(num) && num > 0) {
                    const clamped = Math.min(Math.max(num, min), max);
                    dispatch(setPrizePlacesCount(clamped));
                }
            },
            liveUpdate: false,
        });
    }, [openKeyboard, selectedCategory, customWinners, customParticipants, dispatch]);

    const totalWinners = customWinners || selectedCategory?.minPrizePlacesCount || 0;

    const placeWinners = useMemo(() => {
        const presets = selectedCategory?.presets;
        if (!presets?.length || !totalWinners) return [];
        if (
            Array.isArray(customPlaceWinners)
            && customPlaceWinners.length === presets.length
            && isMatchingPlaceTotals(customPlaceWinners, totalWinners, 0)
        ) {
            return customPlaceWinners.map((n) => Number(n) || 0);
        }
        return distributeWinnersByPercent(presets, totalWinners);
    }, [selectedCategory, totalWinners, customPlaceWinners]);

    const placeBudgets = useMemo(() => {
        const presets = selectedCategory?.presets;
        if (!presets?.length || !budget) return [];
        if (
            Array.isArray(customPlaceBudgets)
            && customPlaceBudgets.length === presets.length
            && isMatchingPlaceTotals(customPlaceBudgets, budget)
        ) {
            return customPlaceBudgets.map((n) => Number(n) || 0);
        }
        return distributeBudgetByPercent(presets, budget);
    }, [selectedCategory, budget, customPlaceBudgets]);

    const placeWinPercents = useMemo(
        () => (placeWinners.length ? distributePercentsFromCounts(placeWinners) : []),
        [placeWinners]
    );

    const placeBudgetPercents = useMemo(
        () => (placeBudgets.length ? distributePercentsFromCounts(placeBudgets) : []),
        [placeBudgets]
    );

    const handleEditPlaceWinners = useCallback((placeIndex) => {
        if (!selectedCategory?.presets?.length || !totalWinners) return;

        const placesCount = selectedCategory.presets.length;
        const placeWinnersRef = { current: placeWinners };

        const applyDigits = (val, index, counts) => {
            const digits = String(val || '').replace(/[^\d]/g, '');
            if (!digits) return counts;
            return redistributePlaceWinner(counts, index, digits, totalWinners);
        };

        const openForPlace = (index) => {
            setActiveEdit({ type: 'winners', index });
            openKeyboard({
                type: 'winners-pad',
                value: '',
                label: `Place ${index + 1} winners (min 1%)`,
                onChange: (val) => {
                    const next = applyDigits(val, index, placeWinnersRef.current);
                    placeWinnersRef.current = next;
                    dispatch(setCustomPlaceWinners(next));
                    setActiveEdit(null);
                },
                onSubmit: () => {
                    setActiveEdit(null);
                },
                onNavigate: (direction, currentValue) => {
                    let nextCounts = applyDigits(currentValue, index, placeWinnersRef.current);
                    if (nextCounts !== placeWinnersRef.current) {
                        placeWinnersRef.current = nextCounts;
                        dispatch(setCustomPlaceWinners(nextCounts));
                    }

                    let nextIndex = index;
                    if (direction === 'first') nextIndex = 0;
                    else if (direction === 'up') nextIndex = Math.max(0, index - 1);
                    else if (direction === 'down') nextIndex = Math.min(placesCount - 1, index + 1);

                    openForPlace(nextIndex);
                },
                liveUpdate: false,
            });
        };

        openForPlace(placeIndex);
    }, [openKeyboard, selectedCategory, totalWinners, placeWinners, dispatch]);

    const handleEditPlaceBudget = useCallback((placeIndex) => {
        if (!selectedCategory?.presets?.length || !budget) return;

        const placesCount = selectedCategory.presets.length;
        const placeBudgetsRef = { current: placeBudgets };

        const applyValue = (val, index, amounts) => {
            const raw = String(val || '').replace(/[^\d.,]/g, '');
            if (!raw) return amounts;
            return redistributePlaceBudget(amounts, index, raw, budget);
        };

        const openForPlace = (index) => {
            setActiveEdit({ type: 'budget', index });
            openKeyboard({
                type: 'winners-pad',
                value: '',
                label: `Place ${index + 1} budget (min 1%)`,
                onChange: (val) => {
                    const next = applyValue(val, index, placeBudgetsRef.current);
                    placeBudgetsRef.current = next;
                    dispatch(setCustomPlaceBudgets(next));
                    setActiveEdit(null);
                },
                onSubmit: () => {
                    setActiveEdit(null);
                },
                onNavigate: (direction, currentValue) => {
                    let nextAmounts = applyValue(currentValue, index, placeBudgetsRef.current);
                    if (nextAmounts !== placeBudgetsRef.current) {
                        placeBudgetsRef.current = nextAmounts;
                        dispatch(setCustomPlaceBudgets(nextAmounts));
                    }

                    let nextIndex = index;
                    if (direction === 'first') nextIndex = 0;
                    else if (direction === 'up') nextIndex = Math.max(0, index - 1);
                    else if (direction === 'down') nextIndex = Math.min(placesCount - 1, index + 1);

                    openForPlace(nextIndex);
                },
                liveUpdate: false,
            });
        };

        openForPlace(placeIndex);
    }, [openKeyboard, selectedCategory, budget, placeBudgets, dispatch]);

    // Вспомогательные компоненты
    const TimeSquares = ({ value }) => (
        <div className="w-[24px] h-[24px] text-[12px] font-roboto text-white flex items-center justify-center bg-white/15 rounded-[4px]">
            {value}
        </div>
    );

    const Time = () => (
        <div className="w-full h-[24px] flex flex-col">
            <div className='w-full flex flex-row items-center justify-between'>
                <div></div>
                <span className="text-[14px] font-semibold text-[#D8D8D8]">Promo by</span>
                <motion.button
                    type="button"
                    onClick={() => setCompactCategoryView(v => !v)}
                    className="cursor-pointer -translate-x-1"
                    animate={{ rotate: compactCategoryView ? 180 : 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                    <AccordionArrow />
                </motion.button>
            </div>
        </div>
    );

    const Promoter = () => (
        <div className="w-[46px] h-[44px] flex items-center gap-[15px]">
            <div className="w-[44px] h-[44px] rounded-full relative">
                <img
                    width={44}
                    height={44}
                    src={getPromoterAvatarSrc(link, data)}
                    onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = getPromoterAvatarSrc(link || data?.link, { ...data, logo: null, logoPath: null });
                    }}
                    className="border border-[#3B3836] rounded-full object-cover w-[44px] h-[44px]"
                    alt="Promoter"
                />
                <img
                    width={16}
                    height={16}
                    src={getIcon('octo')}
                    className="absolute bottom-0 right-0 rounded-full"
                    alt="Badge"
                />
            </div>
        </div>
    );

    // Skeleton для строк таблицы
    const SkeletonRow = () => (
        <div className={`w-full grid grid-cols-6 gap-0 pl-2 border-t border-[#353232]`}>
            <div className="h-[27px] p-[7px] flex flex-row justify-center items-center shrink-0">
                <div className="w-[33px] h-[33px] bg-white/10 rounded animate-pulse" />
            </div>
            <div className="h-[27px] py-[7px] flex justify-center items-center">
                <div className="h-[12px] w-[24px] bg-white/10 rounded animate-pulse" />
            </div>
            <div className="h-[27px] py-[7px] flex justify-center items-center">
                <div className="h-[12px] w-[24px] bg-white/10 rounded animate-pulse" />
            </div>
            <div className="h-[27px] py-[7px] flex justify-center items-center">
                <div className="h-[12px] w-[24px] bg-white/10 rounded animate-pulse" />
            </div>
            <div className="h-[27px] py-[7px] flex justify-center items-center">
                <div className="h-[12px] w-[32px] bg-white/10 rounded animate-pulse" />
            </div>
            <div className="h-[27px] py-[7px] flex justify-center items-center">
                <div className="h-[12px] w-[32px] bg-white/10 rounded animate-pulse" />
            </div>
        </div>
    );

    // Цвета для позиций
    const positionColors = ['#BC3EA4', '#DA2223', '#1FAFC3', '#1AC152', '#FFB700', '#B3A4A4', '#894F24'];
    
    // Функция для получения цвета в зависимости от количества позиций
    const getColorForPosition = (index, totalCount) => {
        let startIndex = 0;
        if (totalCount === 5) {
            startIndex = 2; // Начинаем с 1FAFC3
        } else if (totalCount === 3) {
            startIndex = 4; // Начинаем с FFB700
        }
        
        return positionColors[startIndex + index] || '#EAEAEA';
    };

    const toRoman = (num) => {
        const romanNumerals = [
            { value: 10, symbol: 'X' },
            { value: 9, symbol: 'IX' },
            { value: 5, symbol: 'V' },
            { value: 4, symbol: 'IV' },
            { value: 1, symbol: 'I' }
        ];
        
        let result = '';
        let n = num;
        for (const { value, symbol } of romanNumerals) {
            while (n >= value) {
                result += symbol;
                n -= value;
            }
        }
        return result;
    };

    const formatRewardModalCoinBadgeCount = (amount) => {
        if (amount == null || Number.isNaN(Number(amount))) return 0;
        const num = Number(amount);
        const abs = Math.abs(num);
        let formatted;
        if (abs >= 10_000) formatted = String(Math.round(abs));
        else if (abs >= 1_000) formatted = abs.toFixed(1).replace(/\.0$/, '');
        else if (abs >= 100) formatted = abs.toFixed(2).replace(/\.?0+$/, '');
        else if (abs >= 10) formatted = abs.toFixed(3).replace(/\.?0+$/, '');
        else if (abs >= 1) formatted = abs.toFixed(4).replace(/\.?0+$/, '');
        else if (abs > 0) formatted = abs.toFixed(8).replace(/\.?0+$/, '');
        else formatted = '0';

        if (abs >= 1000) {
            const [intPart, decPart] = formatted.split('.');
            const withSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
            formatted = decPart !== undefined ? `${withSpaces}.${decPart}` : withSpaces;
        }

        return num < 0 ? `-${formatted}` : formatted;
    };

    const PresetDetailModal = ({ modalData, onClose }) => {
        const presets = selectedCategory?.presets || [];
        const [slideIndex, setSlideIndex] = useState(0);
        const [direction, setDirection] = useState(0);
        const [showSwipeHint, setShowSwipeHint] = useState(false);

        useEffect(() => {
            if (!modalData) return;
            const list = selectedCategory?.presets || [];
            const idx = list.findIndex((p) => p === modalData.preset);
            setSlideIndex(idx >= 0 ? idx : modalData.iconId ?? 0);
            setDirection(0);
            setShowSwipeHint(list.length > 1);

            if (list.length <= 1) return undefined;
            const timer = setTimeout(() => setShowSwipeHint(false), 2500);
            return () => clearTimeout(timer);
        }, [modalData]);

        if (!modalData || presets.length === 0) return null;

        const reversedOrder = Boolean(modalData.reversedOrder);
        const preset = presets[slideIndex];
        const winnersCount = placeWinners[slideIndex] ?? 0;
        const budgetAmount = placeBudgets[slideIndex] ?? 0;
        const rewardAmount = winnersCount > 0 ? budgetAmount / winnersCount : 0;
        const winPercent = totalWinners > 0
            ? Math.max(1, Math.round((winnersCount / totalWinners) * 100))
            : (preset?.win || 0);
        const tokenSymbol = foundToken?.symbol || foundToken?.name || selectedBlockchain || 'ETH';
        const rewardTier = getCrystalRarityTier(slideIndex, presets.length);
        const badgeColor = getColorForPosition(slideIndex, presets.length);

        const goToSlide = (nextIndex) => {
            if (presets.length <= 1) return;
            let target = nextIndex;
            if (target < 0) target = presets.length - 1;
            if (target >= presets.length) target = 0;
            if (target === slideIndex) return;

            setShowSwipeHint(false);
            let dir;
            if (slideIndex === presets.length - 1 && target === 0) dir = 1;
            else if (slideIndex === 0 && target === presets.length - 1) dir = -1;
            else dir = target > slideIndex ? 1 : -1;
            if (reversedOrder) dir = -dir;

            setDirection(dir);
            setSlideIndex(target);
        };

        const goNext = () => goToSlide(reversedOrder ? slideIndex - 1 : slideIndex + 1);
        const goPrev = () => goToSlide(reversedOrder ? slideIndex + 1 : slideIndex - 1);

        const slideVariants = {
            enter: (dir) => ({
                x: dir === 0 ? 0 : dir > 0 ? 80 : -80,
                opacity: dir === 0 ? 1 : 0,
            }),
            center: { x: 0, opacity: 1 },
            exit: (dir) => ({
                x: dir > 0 ? -80 : 80,
                opacity: 0,
            }),
        };

        return (
            <motion.div
                className="fixed inset-0 z-50 flex justify-center px-4 pb-4 pt-[70%]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
            >
                <motion.div
                    className=" absolute inset-0 bg-[#2C2C2CE5]/90 backdrop-blur-sm "
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                />
                <motion.div
                    className="relative z-10 w-full max-w-[300px] h-[300px] px-3  "
                    initial={{ opacity: 0, scale: 0.92, y: 16 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.92, y: 16 }}
                    transition={{ duration: 0.25, ease: 'easeOut' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <AnimatePresence initial={false} custom={direction} mode="wait">
                        <motion.div
                            key={slideIndex}
                            custom={direction}
                            variants={slideVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{ duration: 0.25, ease: 'easeOut' }}
                            drag="x"
                            dragConstraints={{ left: 0, right: 0 }}
                            dragElastic={0.15}
                            onDragEnd={(_, info) => {
                                if (info.offset.x < -30 || info.velocity.x < -30) goNext();
                                else if (info.offset.x > 30 || info.velocity.x > 30) goPrev();
                            }}
                            className="relative flex flex-col items-center touch-pan-y cursor-grab active:cursor-grabbing"
                        >
                            <div className='translate-y-10 bg-gradient-to-b from-[#353232] to-[#232323] absolute -bottom-2 left-0 right-0 m-auto w-[200px] h-[170px] rounded-[8px] z-0' />
                            <div className='flex flex-col items-center z-10 translate-y-10'>
                                <div className='flex items-center justify-center relative'>
                                    <CrownIcon width={100} height={72} />
                                    <div
                                        className="absolute top-0 left-0 right-0 m-auto bg-black w-[45px] h-[45px] rounded-full flex items-center justify-center"
                                        style={{ borderColor: badgeColor, borderWidth: '4px' }}
                                    >
                                        <span className="text-[22px] font-bold" style={{ color: badgeColor }}>
                                            {toRoman(slideIndex + 1)}
                                        </span>
                                    </div>
                                </div>
                                <span className="text-[20px] font-roboto text-white mb-2">
                                    {rewardTier.name} reward
                                </span>
                                <CoinBadge
                                    token={foundToken ? `https://api.duotetra.quest/${foundToken.logoPath}` : undefined}
                                    icon={foundToken ? undefined : (selectedBlockchain || 'ETH')}
                                    count={formatRewardModalCoinBadgeCount(rewardAmount)}
                                    countOnly
                                    isCenter
                                    fixedWidth={150}
                                    customColorText={"#DEC315"}
                                    fullCount
                                />
                                <div className="flex items-center gap-1 my-2">
                                    <img
                                        width={24}
                                        height={24}
                                        src={foundToken ? `https://api.duotetra.quest/${foundToken.logoPath}` : getIcon(selectedBlockchain || 'ETH')}
                                        alt={tokenSymbol}
                                        className={`flex-shrink-0 object-contain ${foundToken ? 'rounded-full' : ''}`}
                                        draggable={false}
                                    />
                                    <span className="text-white text-[12px] font-semibold truncate max-w-[120px]">
                                        {tokenSymbol}
                                    </span>
                                </div>
                                <span className="text-white text-[12px]">
                                    {winPercent}% chance
                                </span>
                            </div>
                        </motion.div>
                    </AnimatePresence>

                    <AnimatePresence>
                        {showSwipeHint && presets.length > 1 && (
                            <motion.div
                                className="flex justify-center mt-6 pointer-events-none"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                <Lottie
                                    animationData={swipeAnim}
                                    loop
                                    style={{ width: 72, height: 72 }}
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>
            </motion.div>
        );
    };

    const TableItem = ({iconId, preset, isLast, index, totalCount}) => {
        if (!selectedCategory || !preset) return null;
        
        // Первый элемент = I, второй = II и т.д.
        const romanPosition = toRoman(index + 1);
        const color = getColorForPosition(index, totalCount);
        const winnersCount = placeWinners[index] ?? 0;
        const budgetAmount = placeBudgets[index] ?? 0;
        const winPercentDisplay = totalWinners > 0
            ? (placeWinPercents[index] ?? 0)
            : (resolveWinPercents(selectedCategory.presets)[index] || 0);
        const budgetPercentDisplay = budget > 0
            ? (placeBudgetPercents[index] ?? 0)
            : (resolveBudgetPercents(selectedCategory.presets)[index] || 0);
        
        return(
            <div
                data-place-row={index}
                className={`w-full flex gap-0  border-t ${isLast ? '' : ''} border-[#353232]`}
            >
                <div
                    className="w-[15%] h-[38px] p-[7px] flex flex-row justify-center items-center shrink-0 cursor-pointer active:opacity-80"
                    onClick={() => setActivePresetModal({ preset, iconId: index, reversedOrder: true })}
                >
                    <div 
                        className="translate-x-1 w-[27px] h-[27px] rounded-full flex items-center justify-center"
                        style={{ borderColor: color, borderWidth: '2px' }}
                    >
                        <span className=" text-[14px] font-semibold" style={{ color: color }}>{romanPosition}</span>
                    </div>
                </div>
                <div className="w-[5%] h-[38px]  py-[0px] flex justify-end items-end">
                    <span className={'text-[9px] text-[#959595]'}>{winPercentDisplay ? `${winPercentDisplay}%` : ''}</span>
                </div>
                <div
                    className={`w-[15%] h-[38px] py-[7px] flex justify-center items-center cursor-pointer active:opacity-80 ${
                        activeEdit?.type === 'winners' && activeEdit.index === index
                            ? 'bg-[#2B8FF4]/35'
                            : showTierWinnersGuide
                              ? 'bg-[#2B8FF4]/25 ring-1 ring-inset ring-[#2373BE]'
                              : 'bg-[#252323]'
                    }`}
                    onClick={() => handleEditPlaceWinners(index)}
                >
                    <span className={'text-[12px] text-white underline decoration-white/30 underline-offset-2'}>
                        {winnersCount || ''}
                    </span>
                </div>
                <div className="w-[7%] h-[38px] py-[0px] flex justify-end items-end">
                    <span className={'text-[9px] text-[#959595]'}>{budgetPercentDisplay ? `${budgetPercentDisplay}%` : ''}</span>
                </div>
                <div
                    className={`w-[28%] h-[38px] py-[7px] flex justify-center items-center cursor-pointer active:opacity-80 ${
                        activeEdit?.type === 'budget' && activeEdit.index === index
                            ? 'bg-[#2B8FF4]/35'
                            : showTierBudgetGuide
                              ? 'bg-[#2B8FF4]/25 ring-1 ring-inset ring-[#2373BE]'
                              : 'bg-[#252323]'
                    }`}
                    onClick={() => handleEditPlaceBudget(index)}
                >
                    <span className={'text-[12px] text-white underline decoration-white/30 underline-offset-2'}>
                        {formatPresetBudgetCell(budgetAmount)}
                    </span>
                </div>
                <div
                    className="w-[30%] h-[38px] py-[7px] pl-2 flex justify-start items-center bg-[#1A1704] cursor-pointer active:opacity-80"
                    onClick={() => setActivePresetModal({ preset, iconId: index, reversedOrder: true })}
                >
                    <span className="text-[12px] text-white flex items-center gap-1">
                        <img
                            src={foundToken ? `https://api.duotetra.quest/${foundToken.logoPath}` : getIcon(selectedBlockchain || 'ETH')}
                            className={`icon-img flex-shrink-0 ${foundToken ? 'rounded-full' : ''}`}
                            alt="token"
                            style={{
                                width: 16,
                                height: 16
                            }}
                        />
                        <span className='w-full flex items-center pl-1 text-[#DEC315]'>
                        {(() => {
                            if (!budgetAmount || !winnersCount) return '';
                            const rewardAmount = budgetAmount / winnersCount;
                            return formatTableRewardAmount(rewardAmount);
                        })()}
                        </span>
                    </span>
                </div>
            </div>
        )
    };

    const CategoriesItem = ({ pos, reward, winners, color, onOpen }) => (
        <div
            className="flex flex-col items-center gap-[4px] flex-shrink-0 relative cursor-pointer"
            style={{ minWidth: '42px', maxWidth: '72px' }}
            onClick={(e) => {
                e.stopPropagation();
                onOpen?.();
            }}
        >
            <span className="text-[#DEC315] text-[9px] font-semibold">
                {formatCompactCount(reward)}
            </span>
            <div
                className="w-[28px] h-[28px] rounded-full flex items-center justify-center"
                style={{ borderColor: color, borderWidth: '1.5px' }}
            >
                <span className="text-[11px] font-semibold" style={{ color }}>{toRoman(pos)}</span>
            </div>
            {winners > 0 && (
                <span className="text-white text-[11px] font-semibold">{winners}</span>
            )}
        </div>
    );

    const CompactCategoryView = () => {
        const presets = selectedCategory?.presets || [];

        if (crystalsLoading) {
            return (
                <div className="flex justify-center py-4">
                    <span className="text-[12px] text-[#959595]">Загрузка...</span>
                </div>
            );
        }

        if (!selectedCategory || presets.length === 0) {
            return (
                <div className="flex justify-center items-center py-4">
                    <span className="text-[12px] text-[#959595]">Выберите категорию</span>
                </div>
            );
        }

        const gapClass = presets.length === 5 ? 'gap-4' : presets.length === 3 ? 'gap-7' : 'gap-[3px]';

        return (
            <div className="w-full flex flex-col items-center mt-[8px] mb-2">
                <div
                    className={`w-full flex flex-row items-center justify-center px-2 overflow-x-auto hide-scrollbar ${gapClass}`}
                    style={{ flexWrap: 'nowrap' }}
                >
                    {[...presets].reverse().map((preset, reverseIndex) => {
                        const originalIndex = presets.length - 1 - reverseIndex;
                        const winners = placeWinners[originalIndex] ?? 0;
                        const budgetAmount = placeBudgets[originalIndex] ?? 0;
                        const reward = winners > 0 ? budgetAmount / winners : 0;
                        const color = getColorForPosition(originalIndex, presets.length);

                        return (
                            <div key={originalIndex}>
                                <CategoriesItem
                                    pos={originalIndex + 1}
                                    reward={reward}
                                    winners={winners}
                                    color={color}
                                    onOpen={() => setActivePresetModal({
                                        preset,
                                        iconId: originalIndex,
                                        reversedOrder: true,
                                    })}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const CrystalTable = () => {
        // Header 20 + place rows 33 each + Total row 33 — keeps layout stable for current cat (3/5/7)
        const catCount = selectedCategory?.presets?.length || selectedCategory?.number || 0;
        const tableMinHeight = catCount > 0 ? 20 + catCount * 33 + 33 : undefined;

        return(
            <div className={'relative flex flex-col'} style={tableMinHeight ? { minHeight: tableMinHeight } : undefined}>
                 {/* Full-column Tour targets — tip with placement=bottom sits under all cells */}
                 <div
                     data-guide="tierWinners"
                     aria-hidden
                     className="pointer-events-none absolute top-0 bottom-0 left-[20%] w-[15%] z-[1] rounded-[4px]"
                 />
                 <div
                     data-guide="tierBudget"
                     aria-hidden
                     className="pointer-events-none absolute top-0 bottom-0 left-[42%] w-[28%] z-[1] rounded-[4px]"
                 />
                 <div className={'w-full flex flex-row '}>
                     <div className="w-[20%] h-[20px] flex justify-center items-center">
                         <span className={'text-[10px] text-[#D8D8D8]'}>category</span>
                     </div>
                     {/* <div className="w-[16.67%] h-[20px] flex justify-center items-center">
                         <span className={'text-[10px] text-[#D8D8D8]'}>win %</span>
                     </div> */}
                     <div
                         className={`w-[15%] h-[20px] flex justify-center items-center rounded-[3px] ${
                             showTierWinnersGuide ? 'ring-1 ring-[#2373BE] bg-[#2B8FF4]/20' : ''
                         }`}
                     >
                         <span className={'text-[10px] text-[#D8D8D8]'}>winners</span>
                     </div>
                     <div className="w-[7%] h-[20px] flex justify-center items-center">
                         {/* <span className={'text-[10px] text-[#D8D8D8]'}>budget %</span> */}
                     </div>
                     <div
                         className={`w-[28%] h-[20px] flex justify-center items-center rounded-[3px] ${
                             showTierBudgetGuide ? 'ring-1 ring-[#2373BE] bg-[#2B8FF4]/20' : ''
                         }`}
                     >
                         <span className={'text-[10px] text-[#D8D8D8]'}>prize pool</span>
                     </div>
                     <div className="w-[30%] h-[20px] flex justify-center items-center">
                         <span className={'text-[10px] text-[#D8D8D8]'}>reward</span>
                     </div>
                 </div>
                {crystalsLoading && (
                    Array.from({ length: 7 }).map((_, i) => (
                        <SkeletonRow key={`sk_${i}`} />
                    ))
                )}
                {!crystalsLoading && (!selectedCategory || !selectedCategory.presets) && (
                    <div className="flex justify-center items-center py-4">
                        <span className="text-[12px] text-[#959595]">Выберите категорию</span>
                    </div>
                )}
                {!crystalsLoading && selectedCategory && selectedCategory.presets && (
                    selectedCategory.presets.map((preset, i, arr) => (
                        <TableItem key={i} iconId={preset.iconId || i} preset={preset} isLast={i === arr.length - 1} index={i} totalCount={arr.length} />
                    ))
                )}
                {!crystalsLoading && selectedCategory && selectedCategory.presets && (
                    <div className={`w-full flex items-start justify-between gap-0 border-t border-[#353232]`}>
                        <div className="w-[20%] h-[33px] p-[7px] flex flex-row justify-center items-center shrink-0 relative">
                            <span className={'text-[13px] font-semibold text-white'}>Total</span>
                        </div>
                        <div className="w-[15%] h-[33px] p-[7px] flex flex-row justify-center items-center">
                            <span className={'text-[13px] font-semibold text-white'}>
                                {formatThousands(totalWinners || '')}
                            </span>
                        </div>
                        <div className="w-[7%] h-[33px] p-[7px] flex flex-row justify-center items-center" />
                        <div className="w-[28%] h-[33px] p-[7px] flex flex-row justify-center items-center">
                            <span className={'text-[13px] font-semibold text-[#DEC315]'}>
                                {budget ? formatCompactBudget(budget) : ''}
                            </span>
                        </div>
                        <div className="w-[30%] h-[33px] py-[7px] flex flex-row pl-2 gap-1 items-center bg-[#1A1704]">
                            <img
                                src={foundToken ? `https://api.duotetra.quest/${foundToken.logoPath}` : getIcon(selectedBlockchain || 'ETH')}
                                className={`icon-img flex-shrink-0 ${foundToken ? 'rounded-full' : ''}`}
                                alt="token"
                                style={{
                                    width: 16,
                                    height: 16
                                }}
                            />
                            <span className="flex items-center pl-1 text-[13px] font-semibold text-white min-w-0">
                                <span className="text-[#D8D8D8] text-[12px] font-semi font-sans min-w-0 max-w-[60px] xs:max-w-[40px]">
                                    {(() => {
                                        const tokenName = String(foundToken?.symbol || foundToken?.name || selectedBlockchain || 'ETH');
                                        if (tokenName.length <= 10) return tokenName;
                                        return `${tokenName.slice(0, 10)}...`;
                                    })()}
                                </span>
                            </span>
                        </div>
                    </div>
                )}
            </div>
        )
    };

    const ParticipantsValueRect = ({ customWidthClass, title, count, percentage, onClick, isHideTitle, showDot, dotTouched, highlight }) => (
        <div
            className={`relative ${customWidthClass ? customWidthClass : `w-[70px]`} flex flex-col items-center justify-center gap-[3px] ${onClick ? 'cursor-pointer' : ''} ${highlight ? 'relative z-[80]' : ''}`}
            onClick={onClick}
        >
            {showDot && !dotTouched && (
                <div
                    className="absolute top-2 left-1 w-1.5 h-1.5 rounded-full bg-[#FB2C36] shadow-[0_0_6px_#FB2C36]"
                />
            )}
            {!isHideTitle && (
                <span className="text-white text-[11px]">{title}</span>
            )}
            <div className={`w-full h-[24px] flex items-center justify-center font-roboto text-[13px] rounded-[40px] border
                ${title === 'winners' ? 'border-[#DEC315]' : 'border-white/15'}
                ${highlight ? 'border-[#2373BE]' : ''}
                ${!percentage ? 'bg-[#D3D3D3] text-black' : 'bg-white/20 text-white'}
                ${onClick ? 'active:scale-95 transition-transform' : ''}`}>
                {typeof count === 'number' ? formatThousands(count) : count}
            </div>
            {!isHideTitle && (
                <span className="text-[#959595] text-[11px] font-roboto">
                    {percentage ? `${percentage}%` : '\u00A0'}
                </span>
            )}
        </div>
    );

    const Participants = () => {
        if (!selectedCategory) return null;

        const participantsCount = customParticipants || selectedCategory.minParticipantsCount;
        const winnersCount      = customWinners || selectedCategory.minPrizePlacesCount;
        const nonWinnersCount   = participantsCount - winnersCount;
        const participantsPct   = 100;
        const winnersPct        = participantsCount > 0 ? Math.round(winnersCount / participantsCount * 100) : 0;
        const nonWinnersPct     = participantsCount > 0 ? Math.round(nonWinnersCount / participantsCount * 100) : 0;

        return (
            <div className="w-full px-[16px] py-[6px] flex flex-row items-center justify-between gap-[9px]">
                <div data-guide="players">
                    <ParticipantsValueRect
                        title="players"
                        count={participantsCount}
                        percentage={participantsPct}
                        onClick={handleEditParticipants}
                        showDot
                        dotTouched={playersTouched}
                        highlight={showPlayersGuide}
                    />
                </div>
                <div data-guide="winners">
                    <ParticipantsValueRect
                        title="winners"
                        count={winnersCount}
                        percentage={winnersPct}
                        onClick={handleEditWinners}
                        showDot
                        dotTouched={winnersTouched}
                        highlight={showWinnersGuide}
                    />
                </div>
                <div className='flex flex-col items-center justify-start gap-[3px] h-[62px]'>
                    <span className='text-[11px] font-[400] text-white'>non-winner</span>
                    <div className='w-[80px] h-[24px]  bg-black rounded-full  flex items-center justify-center flex-shrink-0 border border-[#353232] text-white'>
                        <span className='text-[13px] text-[#D8D8D8]'>{formatThousands(nonWinnersCount)}</span>
                    </div>
                    {
                        nonWinnersPct > 1 && (
                            <span className="text-[11px] font-[300] text-[#959595] font-roboto">{nonWinnersPct}%</span>
                        )
                    }
                </div>
            </div>
        );
    };

    let costDisplay;
    if (feeLoading) {
        costDisplay = '...';
    } else if (fee != null) {
        costDisplay = `$${fee}`;
    } else {
        costDisplay = '—';
    }

    return (
        <>
        <div className="w-full h-fit flex flex-col  ">

            <div className="w-full  border border-white/20 rounded-[8px] overflow-hidden">
                <div className='w-full p-[12px]'>
                    <Time />
                    <div className="w-full flex items-center justify-between gap-[12px]">
                        <div className='relative flex-shrink-0' ref={promoterRef}>
                            <div
                                className="cursor-pointer"
                                onClick={() => setShowPromoterTooltip(v => !v)}
                            >
                                <Promoter />
                            </div>

                            <AnimatePresence>
                                {showPromoterTooltip && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -8, scale: 0.95 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: -8, scale: 0.95 }}
                                        transition={{ duration: 0.25, ease: 'easeOut' }}
                                        className={`min-w-[280px] ${data?.name? "h-28" : "h-20"} absolute top-[calc(100%+10px)] -left-2 z-10 bg-[#2373BE] rounded-[10px]`}
                                    >
                                        <div className='w-full h-full relative'>
                                            <div className='w-[12px] h-[7px] bg-[#2373BE] [clip-path:polygon(50%_0%,0%_100%,100%_100%)] absolute -top-[7px] left-[22px]' />
                                            <div className='w-full h-full flex flex-col items-start p-3 gap-1'>
                                                <span className="text-white text-[14px] font-semibold">Promo Creator Name</span>
                                                {
                                                    data?.name && (
                                                        <span className="text-white text-[12px] font-semibold">{data?.name}</span>
                                                    )
                                                }
                                                <span className="text-white/80 text-[13px] truncate max-w-full">Telegram link only: channel / group / bot / account</span>
                                                {
                                                    data?.name && (
                                                        <span className="text-white/80 text-[11px] truncate max-w-full">{link }</span>
                                                    )
                                                }
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                        <div className="flex flex-col gap-[4px] items-start overflow-hidden min-w-0 w-full">
                            <div
                                data-guide="telegramLink"
                                className={`w-full h-[25px] rounded-[4.5px] bg-white/15 flex items-center justify-center relative gap-[6px] py-[3px] pl-3 pr-7 overflow-hidden cursor-pointer ${showLinkGuide ? 'ring-1 ring-[#2373BE]' : ''} ${tgLoading ? 'opacity-50' : ''}`}
                                onClick={handleOpenKeyboard}
                            >
                                {!hasResolvedPromoterLink && (
                                    <div className="absolute left-[8px] top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#FB2C36] shadow-[0_0_6px_#FB2C36] flex-shrink-0" />
                                )}
                                <img
                                    width={12} height={12}
                                    src={getIcon('link')}
                                    className=" flex-shrink-0 absolute right-[9.5px]"
                                />
                                <span className={`${hasResolvedPromoterLink ? 'text-white/90 text-[14px]' : 'text-[#959595] text-[14px]'} truncate flex-1 min-w-0 text-center`}>
                                    {promoterLinkDisplay}
                                </span>
                            </div>
                        </div>
                        <div className="whitespace-nowrap h-[24px] flex flex-shrink-0 justify-end items-center gap-1">
                            <TimeSquares value={selectedDay || 3} /><span className="text-[12px] font-roboto">D</span>
                        </div>
                    </div>

                    <div className='w-full flex justify-between items-center mt-2'>
                        <div className='min-w-[50px] h-[24px] px-1.5 border-[2px] border-[#353232] rounded-[6px] flex flex-row items-center justify-center gap-[4px]'>
                            <SettingsIcon />
                            <span className='text-[12px] text-[#D8D8D8]'>
                                {(selectedCategory?.name ?? '')}
                            </span>
                        </div>

                        <div className='relative' ref={prizePoolRef}>
                            <div
                                className="cursor-pointer"
                                onClick={() => setShowPrizePoolTooltip(v => !v)}
                            >
                                <CoinBadge
                                    token={foundToken ? `https://api.duotetra.quest/${foundToken.logoPath}` : undefined}
                                    icon={foundToken ? undefined : 'ETH'}
                                    count={budget}
                                    classicCount
                                    name={foundToken?.symbol || foundToken?.name || 'ETH'}
                                    iconHeight={18}
                                    iconWidth={18}
                                    isHeader={true}
                                    selectedBlockchain={selectedBlockchain || 'ETH'}
                                    fixedWidth={160}
                                    customTextClass={"translate-x-0.5"}
                                />
                            </div>

                            <AnimatePresence>
                                {showPrizePoolTooltip && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -8, scale: 0.95 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: -8, scale: 0.95 }}
                                        transition={{ duration: 0.25, ease: 'easeOut' }}
                                        className='absolute top-[calc(100%+10px)] -left-7  min-w-[220px] min-h-20 bg-[#2373BE] z-10 rounded-[10px]'
                                    >
                                        <div className='w-full h-full flex flex-col  items-center relative'>
                                            <div className='w-[12px] h-[7px] bg-[#2373BE] [clip-path:polygon(50%_0%,0%_100%,100%_100%)] absolute -top-[6.5px] left-0 right-0 m-auto' />
                                            <div className='w-full flex flex-col gap-1 p-3 text-center'>
                                                <span className='text-white text-[12px] font-semibold'>Giveaway Prize Pool</span>
                                                <span className='text-white/80 text-[12px]'>shared between all winners</span>
                                                <div className='w-full flex items-center justify-center'>
                                                    <span className='text-white text-[11px]'>
                                                        {formatThousands(budget)}{' '}
                                                        {foundToken?.symbol || foundToken?.name || 'ETH'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        <ParticipantsValueRect
                            customWidthClass={`w-[60px]`}
                            title="cost"
                            count={costDisplay}
                            isHideTitle={true}
                        />
                    </div>
                </div>
                
                {compactCategoryView ? <CompactCategoryView /> : <CrystalTable />}
            </div>

            

            <div className="w-full border border-white/20 mt-2 rounded-[8px]">
                <Participants />
            </div>
            
        </div>
        <PromoSnackbar
            message={snackbar.message}
            type={snackbar.type}
            isVisible={snackbar.isVisible}
            isAnimated={snackbarAnimation}
            onClose={closeSnackbar}
        />

        <AnimatePresence>
            {activePresetModal && (
                <PresetDetailModal
                    modalData={activePresetModal}
                    onClose={() => setActivePresetModal(null)}
                />
            )}
        </AnimatePresence>

        </>
    );
}
